import { donutGradient } from "../submissions/submissionsAggregate";
import { formatProgressLabel } from "../../student/assignmentProgressStatus";

export interface ProgressDonutProps {
  readonly complete: number;
  readonly inProgress: number;
  readonly needsHelp: number;
  readonly notStarted: number;
  readonly passRate: number | null;
  /** Extra class on the donut disc (e.g. "overview-donut"). */
  readonly donutClassName?: string;
  /** Hide the built-in legend (e.g. when an external legend is shown). */
  readonly hideLegend?: boolean;
}

function pct(n: number | null): string {
  return n === null ? "-" : `${n}%`;
}

/**
 * Conic-gradient completion donut + status legend.
 * Shared by the teacher analytics + overview dashboards and the public showcase
 * so the chart styling stays in sync wherever progress is displayed.
 */
export default function ProgressDonut({
  complete,
  inProgress,
  needsHelp,
  notStarted,
  passRate,
  donutClassName,
  hideLegend,
}: ProgressDonutProps) {
  const totals = { complete, inProgress, needsHelp, notStarted };
  return (
    <div className="submissions-donut-wrap">
      <div
        className={["submissions-donut", donutClassName].filter(Boolean).join(" ")}
        style={{ background: donutGradient(totals) }}
        role="img"
        aria-label={`Complete ${complete}, in progress ${inProgress}, needs help ${needsHelp}, not started ${notStarted}`}
      >
        <div className="submissions-donut-hole">
          <span className="submissions-donut-pct">{pct(passRate)}</span>
          <span className="submissions-donut-sub">complete</span>
        </div>
      </div>
      {hideLegend ? null : (
        <ul className="submissions-legend">
          <li>
            <span className="submissions-legend-swatch submissions-legend-swatch--complete" />
            {formatProgressLabel("complete")} <strong>{complete.toLocaleString()}</strong>
          </li>
          <li>
            <span className="submissions-legend-swatch submissions-legend-swatch--in-progress" />
            {formatProgressLabel("in_progress")} <strong>{inProgress.toLocaleString()}</strong>
          </li>
          <li>
            <span className="submissions-legend-swatch submissions-legend-swatch--needs-help" />
            {formatProgressLabel("needs_help")} <strong>{needsHelp.toLocaleString()}</strong>
          </li>
          <li>
            <span className="submissions-legend-swatch submissions-legend-swatch--not-started" />
            {formatProgressLabel("not_started")} <strong>{notStarted.toLocaleString()}</strong>
          </li>
        </ul>
      )}
    </div>
  );
}
